import React, { useId, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, MessageCircle, Phone } from 'lucide-react';
import { cn } from '../../../../utils/cn';
import { Button } from '../../../../components/ui/Button';
import { Skeleton } from '../../../../components/ui/Skeleton';
import {
  WA_CONNECTION_LABELS,
  WA_CONNECTION_TONE,
  WA_PURPOSE_LABELS,
  whatsappNumberOptions,
} from '../utils/collectionReminders';

// Donde se conectan y administran los números no oficiales.
export const WHATSAPP_SETTINGS_PATH = '/admin/helpdesk/settings';

const TONE_CHIP = {
  success: 'bg-status-success-soft text-status-success',
  warning: 'bg-status-warning-soft text-status-warning',
  error: 'bg-status-error-soft text-status-error',
  neutral: 'bg-status-neutral-soft text-fg-muted',
};

const Chip = ({ tone = 'neutral', children }) => (
  <span className={cn('inline-flex items-center rounded-full px-1.5 py-0.5 text-[11px] font-medium', TONE_CHIP[tone])}>
    {children}
  </span>
);

const Warning = ({ children }) => (
  <p className="flex items-start gap-1.5 rounded-md bg-status-warning-soft px-2.5 py-1.5 text-xs text-status-warning">
    <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
    <span>{children}</span>
  </p>
);

/**
 * Avisos del número elegido: desconectado (los WhatsApp se omiten) o de solo notificaciones
 * (las respuestas del comercio quedan ocultas en la mesa). Se exporta para los formularios
 * que, con un único número, muestran "WhatsApp sale desde X" en vez del selector: sin esto,
 * ese número se usaba sin ningún aviso.
 *
 * @param {{ option: { usable: boolean, purpose: string } | null | undefined }} props una opción
 *   de `whatsappNumberOptions`
 */
export function SelectedNumberWarnings({ option }) {
  const { t } = useTranslation();
  if (!option) return null;
  return (
    <>
      {!option.usable && (
        <Warning>{t('Este número no está conectado: los WhatsApp se omitirán hasta que se reconecte.')}</Warning>
      )}
      {option.purpose === 'notifications' && (
        <Warning>
          {t('Este número es solo de notificaciones: las respuestas de los comercios quedan ocultas en la mesa de ayuda. Conviene uno con propósito "Mesa de ayuda" o "Ambos".')}
        </Warning>
      )}
    </>
  );
}

/**
 * Selector del número de WhatsApp (no oficial) desde el que salen los recordatorios.
 *
 * - Lista todos los números no inactivos con su conexión y su propósito.
 * - Solo los conectados se pueden elegir de nuevo; el guardado se conserva aunque se haya
 *   desconectado, con aviso (si desapareciera, el operador creería que no hay número).
 * - Advierte el propósito "Notificaciones": la mesa de ayuda oculta esas conversaciones, y
 *   la respuesta del comercio ("ya pagué") quedaría fuera de la vista.
 * - Con varios números, recomienda el mismo con el que la Mesa de ayuda atiende a los
 *   comercios: conversations agrupa por comercio sin mirar el número, así que el recordatorio
 *   cae en el hilo existente y las respuestas del agente salen por el número de ese hilo.
 * - Sin números, lleva a la Mesa de ayuda a conectar uno.
 *
 * @param {{ integrations: object[], loading?: boolean, error?: boolean, onRetry?: () => void,
 *   value: string|null, onChange: (id: string) => void, savedId?: string|null,
 *   label?: string, disabled?: boolean }} props
 */
export default function WhatsappNumberSelect({
  integrations,
  loading = false,
  error = false,
  onRetry,
  value,
  onChange,
  savedId = null,
  label,
  disabled = false,
}) {
  const { t } = useTranslation();
  const name = useId();
  const options = useMemo(() => whatsappNumberOptions(integrations, savedId), [integrations, savedId]);
  const selected = options.find((o) => o.id === value) ?? null;
  const savedMissing = !!savedId && !options.some((o) => o.id === String(savedId));

  if (loading) {
    return (
      <div className="space-y-2" role="status" aria-live="polite">
        <span className="sr-only">{t('Cargando…')}</span>
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-status-error-soft px-3 py-2 text-sm text-status-error" role="alert">
        <span>{t('No se pudieron cargar los números de WhatsApp.')}</span>
        {onRetry && (
          <Button size="sm" variant="outline" onClick={onRetry}>
            {t('Reintentar')}
          </Button>
        )}
      </div>
    );
  }

  if (options.length === 0) {
    return (
      <div className="rounded-md border border-dashed border-border px-3 py-3 text-sm text-fg-muted">
        <p className="mb-1 flex items-center gap-1.5 font-medium text-fg">
          <MessageCircle className="h-4 w-4" aria-hidden />
          {t('No hay números de WhatsApp conectados.')}
        </p>
        <Link to={WHATSAPP_SETTINGS_PATH} className="text-brand-700 underline underline-offset-2">
          {t('Conectá uno en la Mesa de ayuda')}
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div role="radiogroup" aria-label={label ?? t('Número de WhatsApp')} className="space-y-1.5">
        {options.map((o) => {
          const checked = o.id === value;
          const optionDisabled = disabled || !o.selectable;
          return (
            <label
              key={o.id}
              className={cn(
                'flex items-start gap-2.5 rounded-md border px-3 py-2 text-sm transition-colors',
                checked ? 'border-brand-500 bg-brand-50/60' : 'border-border bg-bg',
                optionDisabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:border-brand-500',
              )}
              title={!o.selectable ? t('Solo se pueden elegir números conectados.') : undefined}
            >
              <input
                type="radio"
                name={name}
                className="mt-1 accent-brand-500"
                checked={checked}
                disabled={optionDisabled}
                onChange={() => onChange(o.id)}
              />
              <span className="min-w-0 flex-1">
                <span className="block truncate font-medium text-fg">{o.label}</span>
                <span className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-fg-muted">
                  {o.phone && (
                    <span className="inline-flex items-center gap-1">
                      <Phone className="h-3 w-3" aria-hidden />
                      {o.phone}
                    </span>
                  )}
                  <Chip tone={WA_CONNECTION_TONE[o.connection] ?? 'neutral'}>
                    {t(WA_CONNECTION_LABELS[o.connection] ?? 'Desconectado')}
                  </Chip>
                  <Chip>{t(WA_PURPOSE_LABELS[o.purpose] ?? 'Ambos')}</Chip>
                  {o.isSaved && <Chip tone="neutral">{t('Guardado')}</Chip>}
                </span>
              </span>
            </label>
          );
        })}
      </div>

      <SelectedNumberWarnings option={selected} />
      {savedMissing && <Warning>{t('El número guardado ya no existe o está inactivo: elegí otro.')}</Warning>}
      {options.length > 1 && (
        <p className="text-xs text-fg-muted">
          {t('Conviene usar el mismo número con el que la Mesa de ayuda atiende a tus comercios: si un comercio ya conversa por otro número, el recordatorio queda en ese hilo y las respuestas salen por aquel número.')}
        </p>
      )}

      <Link to={WHATSAPP_SETTINGS_PATH} className="inline-block text-xs text-brand-700 underline underline-offset-2">
        {t('Administrar números en la Mesa de ayuda')}
      </Link>
    </div>
  );
}
